import React, { useState } from 'react';
import exampleLandscape from '@/assets/example-landscape.jpg';
import examplePortrait from '@/assets/example-portrait.jpg';
import exampleProduct from '@/assets/example-product.jpg';
import { useAnalytics } from '@/hooks/useAnalytics';
import { compressorText, Locale } from '@/i18n/compressor';

interface ExampleImagesProps {
  onSelectExample: (file: File) => void;
  compact?: boolean;
  locale?: Locale;
}

const exampleFiles = [
  { src: exampleLandscape, filename: 'paisaje.jpg' },
  { src: examplePortrait, filename: 'retrato.jpg' },
  { src: exampleProduct, filename: 'producto.jpg' },
];

const ExampleImages: React.FC<ExampleImagesProps> = ({ onSelectExample, compact = false, locale = 'es' }) => {
  const [loading, setLoading] = useState<number | null>(null);
  const { trackEvent } = useAnalytics();
  const t = compressorText[locale].exampleImages;
  const examples = [
    { ...exampleFiles[0], label: t.landscape },
    { ...exampleFiles[1], label: t.portrait },
    { ...exampleFiles[2], label: t.product },
  ];

  const handleClick = async (index: number) => {
    if (loading !== null) return;
    setLoading(index);
    const example = examples[index];
    trackEvent('example_image_click', { label: example.label, index });
    try {
      const response = await fetch(example.src);
      const blob = await response.blob();
      const file = new File([blob], example.filename, { type: 'image/jpeg' });
      onSelectExample(file);
    } catch (error) {
      console.error('Error loading example image:', error);
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="text-center">
      {!compact && (
        <h3 className="text-lg font-semibold text-foreground mb-4">
          {t.heading}
        </h3>
      )}
      <div className={`flex justify-center gap-4 ${compact ? 'gap-3' : ''}`}>
        {examples.map((example, index) => (
          <button
            key={example.label}
            onClick={() => handleClick(index)}
            disabled={loading !== null}
            className="group relative rounded-xl overflow-hidden border-2 border-border hover:border-primary/50 transition-all duration-200 hover:shadow-md disabled:opacity-50 w-24 h-24 md:w-28 md:h-28"
          >
            <img
              src={example.src}
              alt={t.exampleAlt(example.label)}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
              loading="lazy"
            />
            <span className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-xs py-1 font-medium">
              {loading === index ? t.loading : example.label}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default ExampleImages;
